import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import type { CognitoUser } from 'amazon-cognito-identity-js';
import {
  signIn as authSignIn,
  signOut as authSignOut,
  completeNewPassword as authCompleteNewPassword,
  signInWithGoogle as authSignInWithGoogle,
  getSession,
  isGoogleSSOEnabled,
  type AuthUser,
} from '@/services/auth';

interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => void;
  signInWithGoogle: () => void;
  completeNewPassword: (newPassword: string) => Promise<void>;
  needsNewPassword: boolean;
  setUserFromOAuth: (user: AuthUser) => void;
  googleSSOEnabled: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pendingCognitoUser, setPendingCognitoUser] = useState<CognitoUser | null>(null);

  useEffect(() => {
    let cancelled = false;
    getSession()
      .then((result) => {
        if (!cancelled && result) {
          setUser(result.user);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      const authUser = await authSignIn(email, password);
      setUser(authUser);
      setPendingCognitoUser(null);
    } catch (err: any) {
      if (err.code === 'NewPasswordRequired') {
        setPendingCognitoUser(err.cognitoUser);
        throw err;
      }
      throw err;
    }
  }, []);

  const completeNewPassword = useCallback(
    async (newPassword: string) => {
      if (!pendingCognitoUser) throw new Error('No pending password challenge');
      const authUser = await authCompleteNewPassword(pendingCognitoUser, newPassword);
      setUser(authUser);
      setPendingCognitoUser(null);
    },
    [pendingCognitoUser],
  );

  const signOut = useCallback(() => {
    authSignOut();
    setUser(null);
    setPendingCognitoUser(null);
  }, []);

  const signInWithGoogle = useCallback(() => {
    authSignInWithGoogle();
  }, []);

  const setUserFromOAuth = useCallback((oauthUser: AuthUser) => {
    setUser(oauthUser);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        signIn,
        signOut,
        signInWithGoogle,
        completeNewPassword,
        needsNewPassword: !!pendingCognitoUser,
        setUserFromOAuth,
        googleSSOEnabled: isGoogleSSOEnabled(),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
